import React, { useState, useEffect } from 'react';

import { isNull } from 'lodash';

import ApprovalSettingsItem from './ApprovalSettingsItem';

import Button from 'CommonComponents/Button';
import LoadingOverlay from 'CommonComponents/LoadingOverlay';

import { useToast } from 'Hooks/useToast';

export default function ApprovalSettingsList(props) {
    const [usersList, setUsersList] = useState([]);
    const [settingsList, setSettingsList] = useState([]);
    const [approversBySection, setApproversBySection] = useState({});
    const [errorsBySection, setErrorsBySection] = useState({});
    const [hasErrors, setHasErrors] = useState(false);
    const [request, setRequest] = useState(false);
    const [activeApproverErrors, setActiveApproverErrors] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    useEffect(() => {
        fetchSettings();
    }, []);

    useEffect(() => {
        let res = false;

        Object.values(errorsBySection).forEach((val) => {
            if (val) {
                res = true;
            }
        });

        setHasErrors(res);
    }, [errorsBySection]);

    const fetchSettings = () => {
        setIsLoading(true);
        axios
            .get(props.fetchUrl)
            .then((res) => {
                const { users } = res.data;
                // An embedding page can narrow the list to the chains it owns.
                const settings = props.triggerCodes
                    ? res.data.settings.filter((item) => item.trigger && props.triggerCodes.includes(item.trigger.trigger_code))
                    : res.data.settings;
                setUsersList(users);
                setSettingsList(settings);

                const defaultApproversBySection = {};
                const defaultErrorsBySection = {};

                settings.forEach((item) => {
                    let approversData = JSON.parse(JSON.stringify(item.approvers));
                    approversData = approversData.map((row) => {
                        row.type = row.pivot.type;
                        row.value = isNull(row.pivot.value) ? 0 : row.pivot.value;

                        return row;
                    });

                    defaultApproversBySection[item.id] = approversData;
                    defaultErrorsBySection[item.id] = false;
                });
                setApproversBySection(defaultApproversBySection);
                setErrorsBySection(defaultErrorsBySection);
                setIsLoading(false);
            })
            .catch((e) => {
                setIsLoading(false);
                console.log(e.response);
            });
    };

    const changeIsActive = (itemId, val) => {
        const items = [...settingsList];
        items.forEach((item) => {
            if (item.id === itemId) {
                item.active = val;
            }
        });

        setSettingsList(items);
    };

    const changeType = (itemId, val) => {
        const items = [...settingsList];
        items.forEach((item) => {
            if (item.id === itemId) {
                item.type = val;
            }
        });

        setSettingsList(items);
    };

    const addApprover = (itemId, val) => {
        const approvers = JSON.parse(JSON.stringify(approversBySection));
        const isEmptyObject = Object.keys(val).length === 0 && val.constructor === Object;
        if (!isEmptyObject) {
            approvers[itemId].push(val);
            setApproversBySection(approvers);
        } else {
            // Get the list of already selected approvers for this item
            const selectedApprovers = approvers[itemId] || [];

            // Find the first available user (not in selectedApprovers)
            const firstAvailableUser = usersList.find((user) => !selectedApprovers.some((approver) => approver.id === user.id));

            if (firstAvailableUser) {
                approvers[itemId].push(firstAvailableUser);
                setApproversBySection(approvers);
            }
        }
    };

    const removeApprover = (itemId, index) => {
        const approvers = JSON.parse(JSON.stringify(approversBySection));
        approvers[itemId] = approvers[itemId].filter((el, i) => {
            return i !== index;
        });

        setApproversBySection(approvers);
    };

    const changeApprover = (itemId, val, index) => {
        const approvers = JSON.parse(JSON.stringify(approversBySection));
        approvers[itemId][index] = val;
        setApproversBySection(approvers);
    };

    const changeErrors = (itemId, val) => {
        const errors = { ...errorsBySection };
        errors[itemId] = val;
        setErrorsBySection(errors);
    };

    const defaultSettingData = () => {
        return {
            id: null,
            active: 0,
            type: '',
            approvers: [],
        };
    };

    const prepareData = () => {
        const data = [];

        settingsList.forEach((item) => {
            const id = item.id;
            const itemData = defaultSettingData();

            itemData.id = id;
            itemData.type = item.type;
            itemData.active = item.active;
            itemData.approvers = [...approversBySection[id]];

            data.push(itemData);
        });

        return data;
    };

    const saveSettings = () => {
        const data = { settings: prepareData() };
        setRequest(true);

        axios
            .put(props.updateUrl, data)
            .then((response) => {
                fetchSettings();

                if (props.onSaved) {
                    props.onSaved(response.data);
                } else {
                    window.location.reload();
                }
            })
            .catch((e) => {
                toast.error(e.response?.data?.message || e.response?.data?.error || $t('The approval settings could not be saved.'));
            })
            .finally(() => {
                setRequest(false);
            });
    };

    return (
        <div className="table__card tw-space-y-6">
            <div className="tw-flex tw-justify-between tw-items-center">
                {/* An embedding page that already heads the section hides this one. */}
                {!props.hideTitle && <h4 className="table__header">{$t('Commitment Management')}</h4>}
                <div className="tw-flex tw-gap-x-1.5 tw-ms-auto">
                    <Button variant="danger" className="tw-mx-1" onClick={fetchSettings}>
                        {$t('Cancel')}
                    </Button>
                    <Button variant="primary" className="tw-mx-1" disabled={activeApproverErrors || request} onClick={saveSettings}>
                        {$t('Save')}
                    </Button>
                </div>
            </div>
            {settingsList.map(
                (item) =>
                    item.trigger &&
                    item.trigger.trigger_code !== 'cc' && (
                        <ApprovalSettingsItem
                            key={item.id}
                            item={item}
                            usersList={usersList}
                            approversList={approversBySection[item.id] || []}
                            changeIsActive={(val) => {
                                changeIsActive(item.id, val);
                            }}
                            changeType={(val) => {
                                changeType(item.id, val);
                            }}
                            addApprover={(val) => addApprover(item.id, val)}
                            removeApprover={(index) => removeApprover(item.id, index)}
                            changeApprover={(val, index) => changeApprover(item.id, val, index)}
                            changeErrors={(val) => changeErrors(item.id, val)}
                            setActiveApproverErrors={setActiveApproverErrors}
                            hasErrors={hasErrors}
                            request={request}
                            saveSettings={() => saveSettings()}
                            triggerCode={item.trigger.trigger_code}
                        />
                    ),
            )}

            <LoadingOverlay isVisible={isLoading}></LoadingOverlay>
        </div>
    );
}
